import React, { useEffect, useState } from "react";
import {
  Box,
  Avatar,
  TableCell,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import DescriptionIcon from "@mui/icons-material/Description";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BadgeIcon from "@mui/icons-material/Badge";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";

import CustomTable from "src/components/Table/Table";
import { useDispatch, useSelector } from "react-redux";

import {
  getCaptainsListAction,
  updateCaptainStatusAction,
  deleteCaptainAction,
  updateDocumentStatusAction,
} from "src/api/captains";

import CustomBackdrop from "src/components/CustomBackdrop/CustomBackdrop";

export default function Captains() {
  const dispatch = useDispatch();

  const captainsList =
    useSelector((state: any) => state.captains?.captainsList) ||
    { result: { data: [], total: 0 } };

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    pageNo: 1,
    pageSize: 10,
  });

  const [openDocDialog, setOpenDocDialog] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const fetchCaptains = async (payload: any) => {
    try {
      setLoading(true);
      await dispatch(getCaptainsListAction(payload));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaptains(formData);
  }, []);

  const fetchData = (data: any) => {
    const payload = {
      ...formData,
      pageNo: data.pageNumber,
      pageSize: data.pageSize,
    };
    setFormData(payload);
    fetchCaptains(payload);
  };

  const handleStatusChange = async (row: any, status: any) => {
    setLoading(true);
    await dispatch(updateCaptainStatusAction({ id: row.id, status }));
    fetchCaptains(formData);
    setLoading(false);
  };

  const handleDeleteClick = (row: any) => {
    setSelectedRow(row);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    await dispatch(deleteCaptainAction(selectedRow.id));
    fetchCaptains(formData);
    setDeleteOpen(false);
    setSelectedRow(null);
    setLoading(false);
  };

  const columns = [
    { field: "profile", headerName: "", width: 80 },
    { field: "name", headerName: "NAME", width: 180 },
    { field: "phone", headerName: "PHONE", width: 200 },
    { field: "createdAt", headerName: "CREATED AT", width: 220 },
    { field: "status", headerName: "STATUS", width: 150 },
    { field: "action", headerName: "ACTION", width: 100 },
    { field: "docs", headerName: "", width: 160 },
  ];

  const vehicleColumns = [
    { field: "registrationNumber", headerName: "REG NUMBER" },
    { field: "model", headerName: "MODEL" },
    { field: "color", headerName: "COLOR" },
    { field: "seatCapacity", headerName: "SEATS" },
    { field: "insuranceExpiry", headerName: "INSURANCE" },
    { field: "pollutionExpiry", headerName: "PUC" },
  ];

  const actionContent = (column: any, row: any) => {
    if (column === "profile") {
      return (
        <TableCell align="center">
          <Avatar>{row.name?.charAt(0)}</Avatar>
        </TableCell>
      );
    }

    if (column === "status") {
      return (
        <TableCell align="center">
          <FormControl size="small">
            <Select
              value={row.status}
              onChange={(e) =>
                handleStatusChange(row, e.target.value)
              }
              sx={{
                backgroundColor: row.status ? "#dcfce7" : "#fee2e2",
                color: row.status ? "green" : "red",
                height: "32px",
              }}
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </TableCell>
      );
    }

    if (column === "action") {
      return (
        <TableCell align="center">
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDeleteClick(row)}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      );
    }

    if (column === "docs") {
      return (
        <TableCell align="center">
          <Typography
            sx={{
              textDecoration: "underline",
              cursor: "pointer",
              color: "#1976d2",
              fontWeight: 500,
            }}
            onClick={() => {
              setDocuments(row.documents || []);
              setOpenDocDialog(true);
            }}
          >
            Show Documents
          </Typography>
        </TableCell>
      );
    }

    return <TableCell align="center">{row[column]}</TableCell>;
  };

  return (
    <>
      {loading && <CustomBackdrop open={loading} />}

      {/* ✅ EXACT SAME FIX AS PARENTS */}
      <Box mt={3} sx={{ pt: 1 }}>
        <CustomTable
          rows={captainsList?.result?.data || []}
          columns={columns}
          total={captainsList?.result?.total || 0}
          expandable
          expandColumns={vehicleColumns}
          expandDataKey="vehicles"
          action={actionContent}
          fetchData={fetchData}
          resetPagination={() => {}}
        />
      </Box>

      {/* DOCUMENTS */}
<Dialog
  open={openDocDialog}
  onClose={() => setOpenDocDialog(false)}
  fullWidth
  maxWidth="sm"
>
  {/* HEADER */}
  <DialogTitle
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontWeight: 700,
      fontSize: "20px",
    }}
  >
    Documents

    <IconButton onClick={() => setOpenDocDialog(false)}>
      <CloseIcon />
    </IconButton>
  </DialogTitle>

  {/* CONTENT */}
  <DialogContent>
    {documents.length === 0 && (
      <Typography>No documents available</Typography>
    )}

    <Box display="flex" flexDirection="column" gap={2}>
      {documents.map((doc: any, index: number) => {
        const getIcon = () => {
          if (doc.type === "Driving License") return <BadgeIcon color="primary" />;
          if (doc.type === "RC") return <DirectionsCarIcon color="primary" />;
          return <DescriptionIcon color="primary" />;
        };

        return (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 2,
              p: 2,
              borderRadius: "14px",
              backgroundColor: "#fff",
              boxShadow: "0px 4px 14px rgba(0,0,0,0.08)",
              border: "1px solid #eee",
              maxWidth: "420px",
            }}
          >
            {/* ICON */}
            <Box mt={0.5}>{getIcon()}</Box>

            {/* CONTENT */}
            <Box flex={1}>
              <Typography fontWeight={600} fontSize={15}>
                {doc.type}
              </Typography>

              {/* IMAGE */}
              {doc.image && (
                <Box mt={1}>
                  <img
                    src={doc.image}
                    alt=""
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      maxHeight: 140,
                      objectFit: "cover",
                    }}
                  />
                </Box>
              )}

              {/* ACTIONS */}
              <Box mt={1.5}>
                {doc.status === null && (
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      sx={{ textTransform: "none" }}
                      onClick={() =>
                        dispatch(
                          updateDocumentStatusAction({
                            id: doc.id,
                            status: 1,
                          })
                        )
                      }
                    >
                      Accept
                    </Button>

                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      sx={{ textTransform: "none" }}
                      onClick={() =>
                        dispatch(
                          updateDocumentStatusAction({
                            id: doc.id,
                            status: 0,
                          })
                        )
                      }
                    >
                      Reject
                    </Button>
                  </Box>
                )}

                {/* STATUS */}
                {doc.status === 1 && (
                  <Typography
                    sx={{
                      color: "green",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    Accepted
                  </Typography>
                )}

                {doc.status === 0 && (
                  <Typography
                    sx={{
                      color: "red",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    Rejected
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  </DialogContent>
</Dialog>

      {/* DELETE DIALOG (MATCHED) */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Confirmation</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{selectedRow?.name}</strong>?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}